import Showdown from 'showdown';
import pkg from 'yaml';
const { parse } = pkg;
import { writable } from 'svelte/store';
import { v4 as uuidv4 } from 'uuid';
import { setLocale, t } from '$lib/i18n.svelte.js';
import { goto } from '$app/navigation';

export const toasts = writable([]);

export const removeToast = (/** @type {string} */ id) => {
	//@ts-ignore
	toasts.update((all) => all.filter((t) => t.id !== id));
};

/**
 * @param {string} message
 * @param {string} type
 * @param {number} timeout
 * @param {() => void} [onClick]
 * @param {string} [position='bottom-right']
 * @param {boolean} [isLoading=false]
 */
export const toastDisplay = (message, type = 'neutral', timeout = 5000, onClick, position = 'bottom-right', isLoading = false) => {
	const id = uuidv4();
	//@ts-ignore
	toasts.update((all) => [...all, { id, message, type, onClick, position, isLoading }]);
	if (timeout > 0 && !isLoading) {
		setTimeout(() => removeToast(id), timeout);
	}
	return id;
};

//@ts-ignore
if (!JSON.tryParse) {
	//@ts-ignore
	JSON.tryParse = (str, defaultValue) => {
		try {
			return JSON.parse(str);
		} catch (e) {
			return defaultValue;
		}
	};
}

/**
 * @param {(arg0: any) => any} task
 * @param {any} ms
 */
export const debounce = (task, ms) => {
	// eslint-disable-next-line no-unused-vars
	let t = { promise: null, cancel: () => void 0 };
	//@ts-ignore
	return async (...args) => {
		try {
			t.cancel();
			//@ts-ignore
			t = deferred(ms);
			await t.promise;
			//@ts-ignore
			await task(...args);
		} catch (_) {
			/* prevent memory leak */
		}
	};
}

/** @param {number | undefined} ms*/
const deferred = (ms) => {
	let cancel,
		promise = new Promise((resolve, reject) => {
			cancel = reject;
			setTimeout(resolve, ms);
		});
	return { promise, cancel };
}

var converter = new Showdown.Converter({
	underline: true,
	tables: true,
	ghCodeBlocks: false,
	smoothLivePreview: true,
	disableForced4SpacesIndentedSublists: true
});

export const markdownToHtml = (/** @type {string} */ input) => {
	input = input.replaceAll('<al>', '<p>')
		.replaceAll('</al>', '</p>')
		.replaceAll('<gr>', '<h3>')
		.replaceAll('</gr>', '</h3>');
	const trimmed = input.trim();
	const isHtml = trimmed.startsWith('<') && trimmed.endsWith('>');
	const replaced = input.replace(/```/g, '%%TRIPLEBACK%%');
	let html = (isHtml ? replaced : converter.makeHtml(replaced))
		.replace(/%%TRIPLEBACK%%/g, '```')
		.replace(/<pre><code>([\s\S]*?)<\/code><\/pre>/g, '<p>$1</p>')
		.replace(/<pre>|<\/pre>|<code>|<\/code>/g, '');
	return html;
};

/**
* Returns the ID of the paired comment if part of a replacement; otherwise returns null.
* @param {string} commentId - The ID of the comment to check.
* @param {Array<import('$lib/stores.svelte.js').Comment>} comments - The list of comments.
* @param {string} sourceId - The ID of the source to check.
* @returns {string|null}
*/
export const getPairedCommentId = (commentId, comments, sourceId) => {
   const comment = comments.find(c => c.comment_id === commentId);
   if (!comment) return null;

   // Get all del/ins elements in DOM order
   const editorElement = document.querySelector(`#text-editor-${sourceId}`);
   if (!editorElement) return null;

   // Group all del/ins elements by their comment-id
   const changeGroups = new Map();
   editorElement.querySelectorAll('del[data-comment-id], ins[data-comment-id]').forEach(node => {
	   const cid = node.getAttribute('data-comment-id');
	   const author = node.getAttribute('author') || '';
	   const time = node.getAttribute('datetime') || '';
	   const type = node.tagName.toLowerCase();
	   
	   if (!cid || author !== comment.external_author) return;
	   
	   if (!changeGroups.has(cid)) {
		   changeGroups.set(cid, { type, author, time, firstNode: node });
	   }
   });

   // Get our target group
   const target = changeGroups.get(commentId);
   if (!target) return null;

   // Find the matching pair by comparing DOM position
   for (const [cid, group] of changeGroups) {
	   if (cid === commentId) continue;
	   if (group.author === target.author && group.time === target.time) {
		   // For a del, look for the next ins
		   if (target.type === 'del' && group.type === 'ins' &&
			   target.firstNode.compareDocumentPosition(group.firstNode) & Node.DOCUMENT_POSITION_FOLLOWING) {
			   return cid;
		   }
		   // For an ins, look for the previous del
		   else if (target.type === 'ins' && group.type === 'del' &&
			   target.firstNode.compareDocumentPosition(group.firstNode) & Node.DOCUMENT_POSITION_PRECEDING) {
			   return cid;
		   }
	   }
   }

   return null;
}

export const htmlToMarkdown = (/** @type {string} */ html) => {
	if (!/<[a-z][\s\S]*>/i.test(html)) return html;
	html = html.replace(/<button[^>]*>([^<]*)<\/button>/g, '[$1]');
	const md = converter.makeMarkdown(html);
	return md.replaceAll('\\*', '*');
};

export const removeSpan = (/** @type {string} */ text) => {
	return text.replace(/<span[^>]*>([^<]*)<\/span>/g, '$1');
};

/**
 *
 * @param { number | Date } timestamp
 * @returns {Date | undefined}
 */
export const unixTimeToDate = (timestamp) => {
	if (timestamp instanceof Date) return timestamp;
	try {
		const date = new Date(timestamp * 1000);
		return isNaN(date.getTime()) ? undefined : date;
	} catch (e) {
		console.log('Error converting unixTimeToDate', e);
		return undefined;
	}
};

/**
 * @description Copie le texte dans le presse-papier
 * @param {string} text
 */
export const copyToClipboard = async (text) => {
	try {
		await navigator.clipboard.writeText(text);
		toastDisplay(t('info.copied'), 'neutral');
	} catch {
		console.error('Failed to copy text');
	}
};

/**
 * @description Retourne le texte sans les accents
 * @param {string} str
 */
export const removeDiacritics = (str) => {
	return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

export const formatNumber = (/** @type {{ toString: () => string; }} */ num) => {
	return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * @description Calcule la distance de Levenshtein entre deux chaînes de caractères
 * @param {string} str1
 * @param {string} str2
 * @returns {number}
 */
export const levenshteinDistance = (str1 = '', str2 = '') => {
	str1 = str1.toLowerCase();
	str2 = str2.toLowerCase();

	if (str1.length > str2.length) [str1, str2] = [str2, str1];

	let prevRow = Array(str1.length + 1)
		.fill(0)
		.map((_, idx) => idx);
	let currRow = Array(str1.length + 1).fill(0);

	for (let j = 1; j <= str2.length; j++) {
		currRow[0] = j;
		for (let i = 1; i <= str1.length; i++) {
			const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
			currRow[i] = Math.min(currRow[i - 1] + 1, prevRow[i] + 1, prevRow[i - 1] + indicator);
		}
		[prevRow, currRow] = [currRow, prevRow];
	}

	return prevRow[str1.length];
};

export const setArticleToTitleIfNecessary = (/** @type {string} */ title) => {
	if (
		title.toLowerCase().startsWith(t('sources.tableType').toLowerCase()) ||
		title.toLowerCase().startsWith(t('sources.annexType').toLowerCase()) ||
		title.toLowerCase().startsWith(t('sources.articleType').toLowerCase())
	) {
		return title;
	} else {
		return `${t('sources.articleType')} ${title}`;
	}
};

/** @param {HTMLElement} node */
export const clickOutside = (node) => {
	const handleClick = (/** @type {{ target: Node | null; defaultPrevented: any; }} */ event) => {
		if (node && !node.contains(event.target) && !event.defaultPrevented) {
			// @ts-ignore
			node.dispatchEvent(new CustomEvent('click_outside', node));
		}
	};

	// @ts-ignore
	document.addEventListener('click', handleClick, true);

	return {
		destroy() {
			// @ts-ignore
			document.removeEventListener('click', handleClick, true);
		}
	};
}

export const capitalize = (/** @type {string | null} */ string) => {
	if (string == null) return null;
	return string.charAt(0).toUpperCase() + string.slice(1);
};

export const capitalizeWords = (/** @type {string} */ str) =>
	str
		.split(' ')
		.map((word) => (word.length > 3 ? word[0].toUpperCase() + word.slice(1).toLowerCase() : word))
		.join(' ');

/**
 * Decodes the JWT to get the payload without verifying the signature.
 * @param {string} token The JWT to decode.
 * @returns {Object} The decoded payload of the token.
 */
export const decodeJwt = (token) => {
	try {
		const base64Url = token.split('.')[1];
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map((c) => {
					return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
				})
				.join('')
		);

		return JSON.parse(jsonPayload);
	} catch (error) {
		console.error('Error decoding JWT:', error);
		return {};
	}
}

/**
 *
 */
export const redactionIntent = (/** @type {string} */ input) => {
	// following 2 lines repalce the line below
	// return /^(ecris|redige)/.test(
	const keywordRegex = new RegExp(`^(${t('writing.intent')})`, 'i');
	return keywordRegex.test(
		input
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toLowerCase()
	);
};

export const removeAlpha = (/** @type {string} */ s) => {
	return s.replace(/\D/g, '');
};

export const minifyHTML = (/** @type {string} */ html) => {
	return html
		.replace(/>\s+</g, '><')
		.replace(/<!--[\s\S]*?-->/g, '')
		.replace(/\s+/g, ' ')
		.trim();
};

export const removeHTML = (/** @type {string} */ html) => {
	return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
};

/**@returns {string} */
export const removeAllSpecialBrackets = (/** @type {string | null} */ text) => {
	if (text == null) return text || '...';
	return text.replace(/【.*?】/g, '');
};

/** @param {string} markdown */
export const extractFrontmatter = (markdown) => {
	const match = /---\n([\s\S]+?)\n---/.exec(markdown);
	if (!match) throw new Error('Invalid frontmatter');
	const frontmatter = parse(match[1]);
	const content = markdown.slice(match[0].length);

	return { metadata: frontmatter, content };
}

/**
 * Parses the domain from a given URL, removing the 'www' subdomain if present.
 * @param {string} url - The URL to parse.
 * @returns {string|null} - The parsed domain or null if the input is invalid.
 */
export const parseDomain = (url) => {
	try {
		const { hostname } = new URL(url);
		return hostname.replace(/^www\./, '');
	} catch {
		return null; // Return null for invalid URLs
	}
};

/**
 * @param {string | number | Date} dateString
 */
export const formatRelativeTime = (dateString) => {
	const date = new Date(dateString);
	const now = new Date();
	//@ts-ignore
	const diffInSeconds = Math.floor((now - date) / 1000);

	if (diffInSeconds < 60) return t('local.passedTimeInstant');
	if (diffInSeconds < 3600)
		return `${t('local.passedTime1')}${Math.floor(diffInSeconds / 60)} ${t('local.minute')}${t('local.passedTime2')}`;
	if (diffInSeconds < 86400)
		return `${t('local.passedTime1')}${Math.floor(diffInSeconds / 3600)} ${t('local.hour')}`;
	if (diffInSeconds < 604800)
		return `${t('local.passedTime1')}${Math.floor(diffInSeconds / 86400)} ${t('local.day')}`;

	return date.toLocaleDateString(t('local.region'), {
		year: 'numeric',
		month: 'long',
		day: 'numeric'
	});
}

/**
 * @description Get language from the URL path
 * @returns {string}
 */
const getLanguageFromURL = () => {
	const urlPath = window.location.pathname;

	if (urlPath.startsWith('/app') || urlPath.startsWith('/beta')) {
		const storedLang = localStorage.getItem('lang') || 'fr';
		return storedLang;
	}

	// Extract language from URL for pages like /fr/ or /en/
	const pathSegments = urlPath.split('/').filter(Boolean);
	const supportedLanguages = ['en', 'fr'];
	if (pathSegments.length > 0 && supportedLanguages.includes(pathSegments[0])) {
		return pathSegments[0];
	}

	return 'fr'; // Default fallback
}

/**
 * @description Get language, prioritizing URL path over localStorage
 * @returns {string}
 */
export const getLanguage = () => {
	if (typeof window !== 'undefined') {
		const urlLang = getLanguageFromURL();
		if (urlLang && urlLang !== 'fr') { // If URL has a specific language, use it
			return urlLang;
		}
		
		const storedLang = localStorage.getItem('lang');
		if (storedLang) {
			return storedLang;
		}
	}
	return 'fr'; // Default to French
}

/**
 * @description Get current page language (prioritizes URL over stored preferences)
 * @returns {string}
 */
export const getCurrentPageLanguage = () => {
	if (typeof window !== 'undefined') {
		return getLanguageFromURL();
	}
	return 'fr';
}

/** @param {string} language */
export const setLanguage = (language) => {
	const url = new URL(window.location.href);

	// Skip URL modifications for app routes
	if (url.pathname.startsWith('/app')) {
		localStorage.setItem('lang', language);
		setLocale(language);
		return;
	}

	const pathSegments = url.pathname.split('/').filter(Boolean);
	const supportedLanguages = ['en', 'fr'];
	const currentLanguage = supportedLanguages.includes(pathSegments[0]) ? pathSegments[0] : 'fr';

	if (currentLanguage !== language) {
		const newPath = `/${language}` + url.pathname.slice(currentLanguage.length + 1) + url.search;
		goto(newPath);
	}
}

/**
 * Polyfill for Array.prototype.findLast
 * @param {Array<any>} array - The array to search
 * @param {(value: any, index: number, array: any[]) => boolean} predicate - The function to test each element
 * @param {any} [thisArg] - Optional. Value to use as this when executing predicate.
 * @returns {any} - The last element that satisfies the predicate, or undefined if none found
 */
export const findLast = (array, predicate, thisArg) => {
	if (typeof Array.prototype.findLast === 'function') {
		return array.findLast(predicate, thisArg);
	} else {
		if (array == null) {
			throw new TypeError('"array" is null or not defined');
		}
		if (typeof predicate !== 'function') {
			throw new TypeError('predicate must be a function');
		}
		const list = Object(array);
		for (let i = list.length - 1; i >= 0; i--) {
			if (predicate.call(thisArg, list[i], i, list)) {
				return list[i];
			}
		}
		return undefined;
	}
};



/**
 * Returns the appropriate icon path for a document based on its title and status
 * @param {string} title - The document title
 * @param {string} status - The document status ('failed', 'generating', 'uploading', 'completed', 'editing', etc.)
 * @returns {string} - Path to the appropriate icon
 */
export const getDocumentIcon = (title, status) => {
	if (status === 'failed') {
		return '/icons/v1/close-square.svg';
	}

	if (['generating', 'uploading'].includes(status)) {
		return '/icons/v1/refresh-2.svg';
	}

	if (status === 'editing') {
		return '/icons/v2/edit-02-stroke-rounded.svg';
	}

	if (title.includes('.pdf')) {
		return '/icons/v2/pdf-02-stroke-rounded.svg';
	}

	if (title.includes('.docx') || title.includes('.doc')) {
		return '/icons/v2/doc-02-stroke-rounded.svg';
	}

	return '/icons/v2/file-02-stroke-rounded.svg';
};

/**
 * @param {import('$lib/stores.svelte').Interaction} interaction
 * @returns {string}
 */
export const getConversationIcon = (interaction) => {
	if (interaction?.messages?.[0]?.content?.startsWith('<workflow')) {
		return '/icons/v2/ice-cubes-stroke-rounded.svg';
	}
	if (interaction?.linked_content?.length > 1) {
		return '/icons/v2/layout-grid-stroke-rounded.svg';
	}
	return '/icons/v2/message-multiple-01-stroke-rounded.svg';
};



/**
 * @param {string} type
 * @returns {string}
 */
export const getTypeIcon = (type) => {
	switch (type) {
		case 'text':
			return '/icons/v2/text-stroke-rounded.svg';
		case 'date':
			return '/icons/v2/calendar-03-stroke-rounded.svg';
		case 'boolean':
			return '/icons/v2/checkmark-circle-02-stroke-rounded.svg';
		case 'number':
			return '/icons/v2/dashboard-speed-01-stroke-rounded.svg';
		case 'list':
			return '/icons/v2/left-to-right-list-bullet-stroke-rounded.svg';
		case 'key_value':
			return '/icons/v2/database-locked-stroke-rounded.svg';
		default:
			return '/icons/v2/text-stroke-rounded.svg';
	}
};


/**
 * @param {string} str
 * @returns {string}
 */
export const toHex = (str) => {
	// UTF-8 → Uint8Array → hex string
	return Array.from(new TextEncoder().encode(str))
		.map((b) => b.toString(16).padStart(2, '0'))
		.join('');
};